
import { useQuery } from '@tanstack/react-query';
import { TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { StatsOverview } from '@/components/admin/dashboard/StatsOverview';
import { RecentActivity } from '@/components/admin/dashboard/RecentActivity';
import { UsageCharts } from '@/components/admin/dashboard/UsageCharts';
import { QuickActions } from '@/components/admin/dashboard/QuickActions';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalSessions: number;
  todaySessions: number;
  totalShots: number;
  avgShotQuality: number;
  topPerformers: Array<{
    id: string;
    name: string;
    score: number;
    improvement: number;
  }>;
  recentSessions: Array<{
    id: string;
    playerName: string;
    duration: number;
    shots: number;
    avgQuality: string;
    timestamp: string;
  }>;
  monthlyStats: Array<{
    month: string;
    sessions: number;
    users: number;
    shots: number;
  }>;
}

export function AdminDashboard() {
  const { user, organization } = useAuth();

  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['admin-dashboard-stats', organization?.id],
    queryFn: async () => {
      // This would normally fetch from multiple endpoints
      // For now, returning mock data
      return {
        totalUsers: 45,
        activeUsers: 32,
        totalSessions: 1256,
        todaySessions: 18,
        totalShots: 15420,
        avgShotQuality: 7.8,
        topPerformers: [
          { id: '1', name: 'Alex Johnson', score: 9.2, improvement: 12 },
          { id: '2', name: 'Sarah Chen', score: 8.9, improvement: 8 },
          { id: '3', name: 'Mike Wilson', score: 8.7, improvement: 15 },
          { id: '4', name: 'Emma Davis', score: 8.5, improvement: 5 },
        ],
        recentSessions: [
          {
            id: '1',
            playerName: 'John Smith',
            duration: 45,
            shots: 34,
            avgQuality: 'GOOD',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
          },
          {
            id: '2',
            playerName: 'Lisa Kumar',
            duration: 38,
            shots: 28,
            avgQuality: 'EXCELLENT',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
          },
          {
            id: '3',
            playerName: 'David Brown',
            duration: 52,
            shots: 41,
            avgQuality: 'AVERAGE',
            timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
          }
        ],
        monthlyStats: [
          { month: 'Jan', sessions: 120, users: 28, shots: 1450 },
          { month: 'Feb', sessions: 135, users: 31, shots: 1620 },
          { month: 'Mar', sessions: 148, users: 35, shots: 1780 },
          { month: 'Apr', sessions: 162, users: 38, shots: 1920 },
          { month: 'May', sessions: 175, users: 42, shots: 2100 },
          { month: 'Jun', sessions: 189, users: 45, shots: 2280 }
        ]
      } as DashboardStats;
    },
    enabled: !!organization?.id
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="grid gap-4 md:grid-cols-4">
            {Array(4).fill(0).map((_, j) => (
              <div key={j} className="h-32 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {isAdmin ? 'Admin Dashboard' : 'Coach Dashboard'}
        </h1>
        <p className="text-muted-foreground">
          {isAdmin 
            ? `Overview of ${organization?.name} performance and user activity`
            : 'Monitor your team\'s performance and training progress'
          }
        </p>
      </div>

      {/* Stats Overview */}
      <StatsOverview 
        stats={{
          totalUsers: stats?.totalUsers || 0,
          activeUsers: stats?.activeUsers || 0,
          totalSessions: stats?.totalSessions || 0,
          todaySessions: stats?.todaySessions || 0,
          totalShots: stats?.totalShots || 0,
          avgShotQuality: stats?.avgShotQuality || 0
        }}
        isAdmin={isAdmin}
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Usage Charts */}
        <div className="lg:col-span-2">
          <UsageCharts monthlyStats={stats?.monthlyStats || []} />
        </div>

        {/* Quick Actions */}
        <div>
          <QuickActions isAdmin={isAdmin} />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Top Performers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="mr-2 h-5 w-5" />
              Top Performers
            </CardTitle>
            <CardDescription>
              Players with highest improvement this month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.topPerformers.map((performer, index) => (
                <div key={performer.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                      #{index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{performer.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Score: {performer.score}/10
                      </p>
                    </div>
                  </div>
                  <Badge variant="success">
                    +{performer.improvement}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <RecentActivity sessions={stats?.recentSessions || []} />
      </div>
    </div>
  );
}